import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    puppyInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('puppyInterest', formData.puppyInterest);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4j0lbkucjmktht58510', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          puppyInterest: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('contact.title')} <span className="text-amber-600">{t('contact.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t('contact.subtitle')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-full mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t('contact.phone')}</h3>
                  <p className="text-gray-600">+7 927 955 09 50</p>
                  <p className="text-gray-600">+7 965 939 87 77</p>
                  <p className="text-sm text-gray-500 mt-1">{t('contact.phoneSchedule')}</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-full mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t('contact.email')}</h3>
                  <p className="text-gray-600">23spoon@mail.ru</p>
                  <p className="text-sm text-gray-500 mt-1">{t('contact.emailResponse')}</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-full mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t('contact.location')}</h3>
                  <p className="text-gray-600">{t('contact.locationCity')}</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <p className="text-gray-700 font-semibold mb-3">Мы в социальных сетях:</p>
              <div className="flex space-x-4">
                <a href="https://t.me/storedogmaltypoo" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors cursor-pointer">
                    <i className="ri-telegram-fill text-xl"></i>
                  </div>
                  <span className="text-xs text-gray-600 mt-1">Telegram</span>
                </a>
                <a href="https://vk.com/modnyipuppy" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors cursor-pointer">
                    <i className="ri-vk-fill text-xl"></i>
                  </div>
                  <span className="text-xs text-gray-600 mt-1">ВКонтакте</span>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                  {t('contact.fullName')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                  {t('contact.emailAddress')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                  {t('contact.phoneNumber')} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="puppyInterest" className="block text-gray-700 font-semibold mb-2">
                  {t('contact.interestedIn')} *
                </label>
                <select
                  id="puppyInterest"
                  name="puppyInterest"
                  value={formData.puppyInterest}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="">{t('contact.selectBreed')}</option>
                  <option value="Toy Poodle">{t('contact.toyPoodle')}</option>
                  <option value="Miniature Poodle">{t('contact.miniaturePoodle')}</option>
                  <option value="Maltipoo">{t('contact.maltipoo')}</option>
                  <option value="Not Sure">{t('contact.notSure')}</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                  {t('contact.message')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 {t('contact.charactersLimit')}</p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 flex items-center">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    {t('contact.successMessage')}
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 flex items-center">
                    <i className="ri-error-warning-fill mr-2"></i>
                    {t('contact.errorMessage')}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-600 text-white py-4 rounded-full text-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
